import type { InstanceRef, SeriesRef, StudyRef } from './dicomweb';
import { tagNumber } from './dicomweb';
import type { CsImage, CsViewport } from './globals';
import type { LoaderContext } from './loader';
import { mark } from './perf';
import { renderViewLabels } from './overlay';
import { startStackPrefetch, type StackPrefetcher } from './prefetch';
import { fetchRendered } from './net';
import { carryTransform } from './stackfit';
import { syncViewportLut } from './display/voilut-cs';
import type { CsEnabledElement } from './globals';

export interface StackState {
  series: SeriesRef;
  imageIds: string[];
  currentImageIdIndex: number;
}

interface NewImageDetail {
  image?: CsImage;
  oldImage?: CsImage;
  viewport?: CsViewport;
  enabledElement: CsEnabledElement;
}

const DISPLAY_OPTIONS = { requestType: 'interaction', priority: -5, addToBeginning: true } as const;

/**
 * Images at least this large (mammography, DX/CR) get a server-rendered preview in fast mode while
 * the diagnostic decode runs: a 13 MP J2K mammogram takes ~2 s to decode in the browser, while a
 * viewport-sized rendered JPEG is ~160 KB. The preview is labelled and never measurable.
 */
const PREVIEW_MIN_PIXELS = 4_000_000;
const REFERENCE_LABEL = 'Resolución de referencia';

function locateFrame(series: SeriesRef, index: number): { instance: InstanceRef; frame: number } {
  let remaining = index;
  for (const instance of series.instances) {
    if (remaining < instance.numberOfFrames) return { instance, frame: remaining + 1 };
    remaining -= instance.numberOfFrames;
  }
  return { instance: series.instances[series.instances.length - 1], frame: 1 };
}

/** One image viewport: a cornerstone-enabled element plus its corner overlays and stack. */
export class Viewport {
  readonly element: HTMLDivElement;
  /** Root of the corner/edge text overlay; its contents belong to overlay.ts. */
  readonly overlayRoot: HTMLDivElement;
  private readonly errorBox: HTMLDivElement;
  private stack: StackState | null = null;
  private prefetcher: StackPrefetcher | null = null;
  private readonly buffering: HTMLDivElement;
  private readonly preview: HTMLImageElement;
  private readonly referenceLabel: HTMLDivElement;
  private referenceMode = false;
  private showToken = 0;
  /** Set while showSeries displays its first image with a fresh, already-fitted viewport. */
  private freshViewport = false;
  /** Non-image content (PDF, video, structured report) shown instead of the stack; built by src/display. */
  private custom: { series: SeriesRef; host: HTMLDivElement; dispose: () => void } | null = null;
  onImageRendered: ((viewport: Viewport) => void) | null = null;
  /** Resolves when every image of the current stack is in the cache (used by the benchmark). */
  stackLoaded: Promise<void> = Promise.resolve();

  constructor(
    readonly container: HTMLElement,
    private studyRef: StudyRef,
    readonly ctx: LoaderContext,
  ) {
    this.element = document.createElement('div');
    this.element.className = 'cs-element';
    this.element.addEventListener('contextmenu', (event) => event.preventDefault());
    container.appendChild(this.element);
    this.overlayRoot = document.createElement('div');
    this.overlayRoot.className = 'view-labels';
    this.errorBox = document.createElement('div');
    this.errorBox.className = 'cs-series-error';
    this.errorBox.hidden = true;
    container.append(this.overlayRoot, this.errorBox);
    this.buffering = document.createElement('div');
    this.buffering.className = 'cs-buffering';
    this.preview = document.createElement('img');
    this.preview.className = 'cs-preview';
    this.preview.alt = '';
    this.preview.hidden = true;
    this.referenceLabel = document.createElement('div');
    this.referenceLabel.className = 'cs-reference-label';
    this.referenceLabel.textContent = REFERENCE_LABEL;
    this.referenceLabel.hidden = true;
    container.append(this.preview, this.referenceLabel, this.buffering);
    window.cornerstone.enable(this.element);
    this.element.addEventListener(window.cornerstone.EVENTS.PRE_RENDER, (event) => {
      syncViewportLut((event as CustomEvent<{ enabledElement: CsEnabledElement }>).detail.enabledElement);
    });
    this.element.addEventListener(window.cornerstone.EVENTS.IMAGE_RENDERED, () => this.renderOverlay());
    this.element.addEventListener(window.cornerstone.EVENTS.NEW_IMAGE, (event) => {
      this.carryZoom((event as CustomEvent<NewImageDetail>).detail);
      const index = this.currentIndex();
      if (index !== undefined) this.prefetcher?.focus(index);
    });
  }

  get study(): StudyRef {
    return this.studyRef;
  }

  setStudy(study: StudyRef): void {
    this.studyRef = study;
  }

  showError(title: string, detail: string): void {
    this.errorBox.replaceChildren();
    const strong = document.createElement('strong');
    strong.textContent = title;
    const span = document.createElement('span');
    span.textContent = detail;
    this.errorBox.append(strong, span);
    this.errorBox.hidden = false;
  }

  get currentStack(): StackState | null {
    return this.stack;
  }

  /** Shows a series from `startIndex`, then downloads the rest of it (strategy depends on the load mode). */
  async showSeries(series: SeriesRef, imageIds: string[], startIndex = 0): Promise<void> {
    const { cornerstone, cornerstoneTools } = window;
    this.prefetcher?.cancel();
    this.clearCustom();
    const token = ++this.showToken;
    const index = Math.min(Math.max(startIndex, 0), imageIds.length - 1);
    this.stack = { series, imageIds, currentImageIdIndex: index };
    this.container.dataset.seriesUid = series.seriesInstanceUID;
    this.container.dataset.loaded = 'false';
    this.container.dataset.allLoaded = 'false';
    this.errorBox.hidden = true;
    if (this.ctx.mode === 'fast') void this.showPreview(series, index, token);
    const image = await cornerstone.loadAndCacheImage(imageIds[index], DISPLAY_OPTIONS);
    if (token !== this.showToken) return;
    this.hidePreview();
    this.freshViewport = true;
    try {
      cornerstone.displayImage(this.element, image, cornerstone.getDefaultViewportForImage(this.element, image));
    } finally {
      this.freshViewport = false;
    }
    mark('first-diagnostic');
    cornerstoneTools.clearToolState(this.element, 'stack');
    cornerstoneTools.addStackStateManager(this.element, ['stack', 'playClip']);
    cornerstoneTools.addToolState(this.element, 'stack', this.stack);
    this.container.dataset.loaded = 'true';
    const prefetcher = startStackPrefetch(this.ctx.mode, imageIds, index, (loaded, total) => {
      const percent = Math.floor((loaded / total) * 100);
      this.buffering.textContent = percent < 100 ? `Buffering (${percent})` : '';
    }, () => this.ctx.client.headers());
    this.prefetcher = prefetcher;
    this.stackLoaded = prefetcher.done.then(() => {
      if (this.prefetcher === prefetcher) this.container.dataset.allLoaded = 'true';
    });
  }

  /** Server-rendered, viewport-sized preview of a large image, shown until the diagnostic image is decoded. */
  private async showPreview(series: SeriesRef, index: number, token: number): Promise<void> {
    const { instance, frame } = locateFrame(series, index);
    const rows = tagNumber(instance.metadata, '00280010');
    const columns = tagNumber(instance.metadata, '00280011');
    if (rows * columns < PREVIEW_MIN_PIXELS) return;
    const rect = this.container.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * ratio));
    const height = Math.max(1, Math.round(rect.height * ratio));
    const framePath = instance.numberOfFrames > 1 ? `/frames/${frame}` : '';
    const url = `${this.ctx.client.instanceUrl(series, instance)}${framePath}/rendered?viewport=${width},${height}`;
    try {
      const response = await fetchRendered(url, this.ctx.client.headers());
      if (!response.ok || token !== this.showToken || this.container.dataset.loaded === 'true') return;
      const objectUrl = URL.createObjectURL(await response.blob());
      if (token !== this.showToken || this.container.dataset.loaded === 'true') {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      this.preview.onload = () => {
        URL.revokeObjectURL(objectUrl);
        mark('first-preview');
      };
      this.preview.src = objectUrl;
      this.preview.hidden = false;
      this.referenceLabel.hidden = false;
    } catch (error) {
      console.warn('[viewer] preview failed', error);
    }
  }

  /** Keeps "Resolución de referencia" on screen while this cell shows JPEG reference images. */
  setReferenceMode(on: boolean): void {
    this.referenceMode = on;
    this.referenceLabel.hidden = !on;
  }

  private hidePreview(): void {
    this.preview.hidden = true;
    this.referenceLabel.hidden = !this.referenceMode;
    this.preview.removeAttribute('src');
  }

  currentImage(): CsImage | undefined {
    return window.cornerstone.getEnabledElement(this.element).image;
  }

  /** A stack image of another size keeps the zoom the user had, relative to the fit (see stackfit.ts). */
  private carryZoom(detail: NewImageDetail): void {
    const { image, oldImage, viewport, enabledElement } = detail;
    if (this.freshViewport || !image || !oldImage || !viewport) return;
    const next = carryTransform(enabledElement.canvas, oldImage, image, viewport);
    if (!next) return;
    viewport.scale = next.scale;
    viewport.translation = next.translation;
  }

  resize(): void {
    if (this.currentImage()) window.cornerstone.resize(this.element, true);
  }

  /** Index of the image on screen within the stack (0-based). */
  currentIndex(): number | undefined {
    const data = window.cornerstoneTools.getToolState(this.element, 'stack')?.data[0] as StackState | undefined;
    return data?.currentImageIdIndex;
  }

  private renderOverlay(): void {
    if (!this.currentImage() || !this.stack) return;
    renderViewLabels(this);
    this.onImageRendered?.(this);
  }

  /** Series shown as non-image content (PDF, video, SR), if any. */
  get customSeries(): SeriesRef | null {
    return this.custom?.series ?? null;
  }

  /**
   * Replaces the stack with non-image content: the cornerstone element is reset (so no command acts on a
   * hidden image) and `build` fills a host above it; the function it returns frees what it made.
   * Returns false when a newer showSeries / showCustom superseded this one while `build` ran.
   */
  async showCustom(series: SeriesRef, kind: string, build: (host: HTMLDivElement, isCurrent: () => boolean) => Promise<() => void>): Promise<boolean> {
    this.prefetcher?.cancel();
    this.prefetcher = null;
    this.clearCustom();
    const token = ++this.showToken;
    this.stack = null;
    this.hidePreview();
    this.errorBox.hidden = true;
    this.buffering.textContent = '';
    this.overlayRoot.replaceChildren();
    window.cornerstone.disable(this.element);
    window.cornerstone.enable(this.element);
    const host = document.createElement('div');
    host.className = `display-host display-${kind}`;
    this.container.appendChild(host);
    this.container.dataset.render = kind;
    this.container.dataset.seriesUid = series.seriesInstanceUID;
    this.container.dataset.loaded = 'false';
    const custom: { series: SeriesRef; host: HTMLDivElement; dispose: () => void } = { series, host, dispose: () => undefined };
    this.custom = custom;
    const dispose = await build(host, () => token === this.showToken);
    if (this.custom !== custom) {
      dispose();
      return false;
    }
    custom.dispose = dispose;
    this.container.dataset.loaded = 'true';
    this.container.dataset.allLoaded = 'true';
    return true;
  }

  private clearCustom(): void {
    const custom = this.custom;
    if (!custom) return;
    this.custom = null;
    try {
      custom.dispose();
    } finally {
      custom.host.remove();
      delete this.container.dataset.render;
    }
  }

  destroy(): void {
    this.showToken++;
    this.clearCustom();
    this.prefetcher?.cancel();
    window.cornerstone.disable(this.element);
    this.container.replaceChildren();
  }
}
